import Axios from '../../axiosConfig';
import { BACKEND_URL } from '../../config';
import { configure, runInAction, makeAutoObservable } from 'mobx';
import AuditLogItem, { AuditLogRequest } from '../../types/auditLog';
import ControlDataDto from '../../types/configData';
import LicenseSchema, { LicenseSchemaDto } from '../../types/license';
import HelpTextDto from '../../types/helpText';
import moment from 'moment';

configure({ enforceActions: 'observed' });

export default class AdminModule {
    private auditLogItems: AuditLogItem[] = [];
    private config: ControlDataDto[] = [];
    private licenses: LicenseSchemaDto[] = [];
    private infoTexts: HelpTextDto[] = [];

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public async fetchAuditLog(requestParams: AuditLogRequest): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'auditLog/getAuditLogs',
            data: requestParams,
            withCredentials: true,
        });
        if (response?.status === 200) runInAction(() => (this.auditLogItems = response.data));
    }

    public async fetchConfigData(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'controlData/adminControlData',
            withCredentials: true,
        });
        if (response?.status === 200) runInAction(() => (this.config = response.data));
    }

    public async saveConfigData(configData: ControlDataDto): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'controlData/saveControlData',
            data: configData,
            withCredentials: true,
        });

        const newItem: ControlDataDto = response.data;
        const index = this.configData.findIndex((item) => item.id === newItem.id);
        runInAction(() => {
            if (index > -1) this.config[index] = newItem;
            else this.config.push(newItem);
        });
    }

    public async fetchLicenseSchemas(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'license/licenseschemas',
            withCredentials: true,
        });
        runInAction(() => (this.licenses = response.data));
    }

    public async saveLicenseSchema(licenseSchema: LicenseSchemaDto): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'license/savelicenseschema',
            data: licenseSchema,
            withCredentials: true,
        });

        const newItem: LicenseSchema = response.data;
        const index = this.licenseSchemas.findIndex((item) => item.id === newItem.id);
        runInAction(() => {
            if (index > -1) this.licenses[index] = newItem;
            else this.licenses.push(newItem);
        });
    }

    public async removeLicenseSchema(schemaId: number): Promise<void> {
        await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `license/removeLicenseSchema/${schemaId}`,
            withCredentials: true,
        });

        runInAction(() => {
            this.licenses = this.licenses.filter((license) => license.id !== schemaId);
        });
    }

    public async fetchAllHelpTexts(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'helpText/getAllHelpTexts',
            withCredentials: true,
        });

        const helpTexts: HelpTextDto[] = response.data;

        runInAction(() => {
            this.infoTexts = helpTexts;
        });
    }

    public async updateHelpText(helpText: HelpTextDto): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'helpText/updateHelpText',
            data: helpText,
            withCredentials: true,
        });

        const newItem: HelpTextDto = response.data;
        const index = this.helpTexts.findIndex((item) => item.id === newItem.id);
        runInAction(() => {
            if (index > -1) this.infoTexts[index] = newItem;
        });
    }

    public get auditLog(): AuditLogItem[] {
        return this.auditLogItems;
    }

    public get configData(): ControlDataDto[] {
        return this.config;
    }

    public get licenseSchemas(): LicenseSchemaDto[] {
        return this.licenses;
    }

    public get helpTexts(): HelpTextDto[] {
        return this.infoTexts.slice().sort((a, b) => Number(a.location) - Number(b.location));
    }

    public filteredAuditLog(filter: string): AuditLogItem[] {
        const stringifyItem = (item: AuditLogItem): string =>
            (
                item.action.toString() +
                item.object.toString() +
                item.username.toString() +
                item.status.toString()
            ).toLowerCase();
        return this.auditLog.filter((logItem) => stringifyItem(logItem).indexOf(filter.toLowerCase()) > -1);
    }

    public getLicenseSchemaById(id: number): LicenseSchemaDto | null {
        return this.licenseSchemas.find((license) => license.id === id) || null;
    }

    public isLicenseExpired(endDate: string | Date): boolean {
        const today = moment().startOf('day');
        const end = moment(endDate).startOf('day');
        return today.isAfter(end);
    }

    public hasLicenseStarted(beginDate: Date): boolean {
        const today = moment().startOf('day');
        const begin = moment(beginDate).startOf('day');
        return today.isBefore(begin);
    }

    public countDaysLeft(endDate: string | Date): number {
        const today = moment();
        const end = moment(endDate);
        const difference = Math.ceil(end.diff(today, 'days', true));
        return difference < 0 ? 0 : difference;
    }
}
